import React, { useEffect, useMemo, useState } from "react";
import { api, formatCurrency, formatApiError } from "../lib/api";
import { Loader2, AlertTriangle, Download, DollarSign, TrendingDown, TrendingUp, Scale, ArrowDown } from "lucide-react";
import { toast } from "sonner";

const FEE_CHIPS = [10, 15, 20, 25, 30, 33.33, 35, 40];

/**
 * Modern settlement distribution calculator.
 *
 * Lives inside the unified client detail page. Lets attorneys/paralegals
 * model settlement scenarios with quick-select fee chips, optional
 * liens/medicals, and a live "estimated client net" headline that turns red
 * if Instabridge payoff would exceed 50% of the pre-funding net.
 */
export default function SettlementCalculator({ clientKey, clientName, instabridgePayoffLive }) {
  const [settlement, setSettlement] = useState("");
  const [feePct, setFeePct] = useState(33.33);
  const [liens, setLiens] = useState("");
  const [serverResult, setServerResult] = useState(null);
  const [downloading, setDownloading] = useState(false);

  // Live (client-side) computation for instant feedback
  const live = useMemo(() => {
    const s = Number(settlement || 0);
    const f = s * (feePct / 100);
    const m = Number(liens || 0);
    const netBefore = +(s - f - m).toFixed(2);
    const ibp = Number(instabridgePayoffLive || 0);
    const clientNet = +(netBefore - ibp).toFixed(2);
    const ratio = netBefore > 0 ? ibp / netBefore : 0;
    return { settlement: s, fee: +f.toFixed(2), liens: m, netBefore, clientNet, ibp, ratio };
  }, [settlement, feePct, liens, instabridgePayoffLive]);

  // Debounced server-side recompute (authoritative payoff number)
  useEffect(() => {
    if (!live.settlement) { setServerResult(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/portal/settlement-calc", {
          client_key: clientKey,
          settlement_amount: live.settlement,
          attorney_fee_pct: feePct,
          estimated_liens_medicals: Number(liens || 0),
        });
        setServerResult(data);
      } catch { /* leave live values as fallback */ }
    }, 350);
    return () => clearTimeout(t);
  }, [live.settlement, feePct, liens, clientKey]);

  const downloadPdf = async () => {
    if (!live.settlement) { toast.error("Enter a settlement amount first"); return; }
    setDownloading(true);
    try {
      const t = JSON.parse(localStorage.getItem("instabridge_attorney"))?.session_token
            || JSON.parse(localStorage.getItem("instabridge_admin"))?.access_token;
      const impHeaders = (() => {
        const blob = JSON.parse(localStorage.getItem("instabridge_attorney"));
        return blob?.impersonate_firm_id ? { "X-Impersonate-Firm": blob.impersonate_firm_id } : {};
      })();
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/portal/settlement-calc/pdf`;
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${t}`, ...impHeaders },
        body: JSON.stringify({
          client_key: clientKey,
          settlement_amount: live.settlement,
          attorney_fee_pct: feePct,
          estimated_liens_medicals: Number(liens || 0),
        }),
      });
      if (!res.ok) throw new Error(`PDF returned ${res.status}`);
      const blob = await res.blob();
      const fname = `${(clientName || "client").replace(/\s+/g, "_")}_settlement_breakdown.pdf`;
      const dl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dl; a.download = fname; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(dl);
      toast.success("Breakdown downloaded");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDownloading(false); }
  };

  const r = serverResult || {
    settlement_amount: live.settlement, attorney_fee_pct: feePct, attorney_fee: live.fee,
    estimated_liens_medicals: live.liens, instabridge_payoff: live.ibp,
    net_before_funding: live.netBefore, estimated_client_net: live.clientNet,
    warning: live.netBefore > 0 && live.ratio > 0.5
      ? `Instabridge payoff (${formatCurrency(live.ibp)}) exceeds 50% of estimated client recovery (${formatCurrency(live.netBefore)} before funding).`
      : null,
  };
  const netNegative = r.estimated_client_net < 0;
  const hasWarning = !!r.warning;

  return (
    <div className="grid lg:grid-cols-5 gap-6 mt-2" data-testid="settlement-calculator">
      {/* Inputs panel */}
      <div className="lg:col-span-2 space-y-5">
        <div>
          <label className="text-[11px] uppercase tracking-wider text-[#6B7280] font-medium">Projected settlement</label>
          <div className="relative mt-1.5">
            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
            <input
              inputMode="decimal"
              value={settlement}
              onChange={(e) => setSettlement(e.target.value.replace(/[^0-9.]/g, ""))}
              placeholder="125,000"
              className="w-full h-12 pl-9 pr-3 rounded-lg border border-[#E5E7EB] text-lg tabular-nums font-medium text-[#0A2540] focus:outline-none focus:ring-2 focus:ring-[#0066FF]/20 focus:border-[#0066FF]"
              data-testid="calc-settlement-input"
            />
          </div>
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-wider text-[#6B7280] font-medium">Attorney fee</label>
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            {FEE_CHIPS.map((c) => (
              <button
                key={c} onClick={() => setFeePct(c)}
                className={`h-8 px-3 rounded-md text-xs font-medium tabular-nums transition-colors ${
                  Math.abs(feePct - c) < 0.01
                    ? "bg-[#0A2540] text-white"
                    : "bg-[#F3F4F6] text-[#374151] hover:bg-[#E5E7EB]"
                }`}
                data-testid={`calc-fee-chip-${c}`}
              >{c}%</button>
            ))}
            <div className="relative">
              <input
                inputMode="decimal"
                value={feePct}
                onChange={(e) => setFeePct(Number(e.target.value.replace(/[^0-9.]/g, "") || 0))}
                className="w-16 h-8 px-2 rounded-md border border-[#E5E7EB] text-xs text-[#0A2540] tabular-nums text-center focus:outline-none focus:ring-2 focus:ring-[#0066FF]/20 focus:border-[#0066FF]"
                data-testid="calc-fee-input"
              />
              <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-[#9CA3AF]">%</span>
            </div>
          </div>
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-wider text-[#6B7280] font-medium">Liens / medicals (optional)</label>
          <div className="relative mt-1.5">
            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
            <input
              inputMode="decimal"
              value={liens}
              onChange={(e) => setLiens(e.target.value.replace(/[^0-9.]/g, ""))}
              placeholder="0"
              className="w-full h-10 pl-9 pr-3 rounded-lg border border-[#E5E7EB] text-sm tabular-nums text-[#0A2540] focus:outline-none focus:ring-2 focus:ring-[#0066FF]/20 focus:border-[#0066FF]"
              data-testid="calc-liens-input"
            />
          </div>
          <div className="text-[11px] text-[#6B7280] mt-1">Health-insurance liens, unpaid medicals, etc.</div>
        </div>

        <button onClick={downloadPdf} disabled={downloading || !live.settlement}
          className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-[#E5E7EB] hover:border-[#0066FF] hover:text-[#0066FF] text-[#374151] text-sm font-medium transition-colors disabled:opacity-60"
          data-testid="calc-pdf-btn">
          {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          Export breakdown PDF
        </button>
      </div>

      {/* Results panel */}
      <div className="lg:col-span-3">
        <div className={`rounded-2xl p-6 lg:p-7 text-white transition-colors ${
          netNegative ? "bg-gradient-to-br from-[#7F1D1D] to-[#450A0A]"
                      : "bg-gradient-to-br from-[#0A2540] via-[#0066FF] to-[#001A3D]"
        }`}>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] opacity-80">
            <Scale className="w-3.5 h-3.5" /> Estimated client net
          </div>
          <div className="font-display text-5xl lg:text-6xl font-semibold tracking-tight mt-2 tabular-nums" data-testid="calc-client-net">
            {formatCurrency(r.estimated_client_net || 0)}
          </div>
          <div className="text-xs opacity-80 mt-2">After attorney fee, liens, and Instabridge payoff.</div>
        </div>

        <div className="grid grid-cols-2 gap-2 mt-3">
          <Bar label="Settlement" amount={r.settlement_amount} variant="neutral" testid="bar-settlement" />
          <Bar label={`Attorney fee · ${(r.attorney_fee_pct || 0).toFixed(1)}%`} amount={-Math.abs(r.attorney_fee || 0)} variant="out" testid="bar-fee" />
          <Bar label="Liens / medicals" amount={-Math.abs(r.estimated_liens_medicals || 0)} variant="out" testid="bar-liens" />
          <Bar label="Instabridge payoff" amount={-Math.abs(r.instabridge_payoff || 0)} variant="instabridge" testid="bar-instabridge" />
        </div>

        {hasWarning && (
          <div className="mt-3 flex items-start gap-2 p-3 rounded-lg bg-[#FFFBEB] border border-[#FDE68A] text-[#92400E] text-xs" data-testid="calc-warning">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>{r.warning}</div>
          </div>
        )}
      </div>
    </div>
  );
}

function Bar({ label, amount, variant, testid }) {
  const positive = (amount || 0) >= 0;
  const tone = variant === "instabridge"
    ? { ic: "text-[#0066FF]", bg: "bg-[#EFF6FF]" }
    : variant === "out"
    ? { ic: "text-[#B91C1C]", bg: "bg-[#FEF2F2]" }
    : { ic: "text-[#047857]", bg: "bg-[#ECFDF5]" };
  const Icon = positive ? TrendingUp : (variant === "instabridge" ? ArrowDown : TrendingDown);
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-3" data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] uppercase tracking-wider text-[#6B7280] truncate">{label}</div>
        <div className={`w-6 h-6 rounded-md ${tone.bg} ${tone.ic} flex items-center justify-center flex-shrink-0`}>
          <Icon className="w-3.5 h-3.5" />
        </div>
      </div>
      <div className={`mt-1 font-display text-xl tabular-nums ${positive ? "text-[#0A2540]" : "text-[#0A2540]"}`}>
        {(amount < 0 ? "− " : "")}{formatCurrency(Math.abs(amount || 0))}
      </div>
    </div>
  );
}
